import type { ThemeId, Tradition } from '../theme/tokens';

export type Gender = 'male' | 'female';

/** Billing/entitlement is a separate integration not built yet -- every user is "free" until
 * then. Never settable via PUT /api/me (see docs/PRACTICE_LEVELS.md's "Subscription model"). */
export type Tier = 'free' | 'premium';

/** A single, global, uncapped title derived from lifetime completions across every pillar and
 * tradition -- there is no "max rank" shown, ever, since the underlying count has no ceiling. */
export type PilgrimRank = 'Seeker' | 'Practitioner' | 'Adherent' | 'Adept' | 'Elder' | 'Sage' | 'Luminary';

export interface Me {
  sub: string;
  email: string;
  displayName: string;
  traditions: Tradition[];
  themePreference: ThemeId;
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
  leaderboardOptIn: boolean;
  /** Which standing mascot illustration + greeting voice to show -- cosmetic only. */
  gender: Gender | null;
  /** The one "main practice" among traditions[] to feature first on Home. */
  primaryTradition: Tradition | null;
  /** Every practiceId ever completed -- powers the Practices page's level-by-level unlock. */
  completedPracticeIds: string[];
  tier: Tier;
  /** Streak Shields (capped at 2, identical on both tiers) -- a missed day silently consumes one
   * instead of resetting the streak. Earned via POST /api/reinforcements, never bought. */
  shieldsAvailable: number;
  /** Kept for backward compatibility -- primary UI should prefer practiceMilestone (see the
   * Omnist Development Plan's "Progress and motivation": a spiritual-authority-sounding title
   * was replaced with concrete practice milestones in primary UI). */
  pilgrimRank: PilgrimRank;
  /** A validated IANA timezone name (e.g. "America/New_York"), or null if never set -- used to
   * compute the caller's *local* date for streak/reinforcement purposes. Unset means UTC. */
  timezone: string | null;
  /** Lifetime count of distinct local dates a session was completed on. Independent of
   * currentStreak (which resets on a gap) -- this only ever grows. */
  totalPracticeDays: number;
  /** The largest totalPracticeDays milestone already reached, or 0. */
  practiceMilestone: PracticeMilestone;
  /** Onboarding preferences (Work Package 2) -- optional/nullable, editable any time via
   * PUT /api/me, not onboarding-only. Feed GET /api/today's recommendation. */
  intent: OnboardingIntent | null;
  experienceLevel: ExperienceLevel | null;
  timeBudgetMinutes: number | null;
  /** UI + content language. Drives which translated copy content routes return and which
   * string bundle the app renders chrome in (see docs/LOCALIZATION.md). */
  locale: Locale;
  /** Derived server-side from an env-var email allowlist (never stored, never client-settable)
   * -- true only for the handful of accounts with admin access to /admin. */
  isAdmin: boolean;
}

export type Locale = 'en' | 'es' | 'hi' | 'zh';

export interface AdminUser {
  sub: string;
  email: string | null;
  displayName: string | null;
  tier: Tier;
  traditions: Tradition[];
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
  totalPracticeDays: number;
  leaderboardOptIn: boolean;
  createdAt: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
}

export interface AdminAnalytics {
  totalUsers: number;
  premiumUsers: number;
  freeUsers: number;
  premiumRate: number;
  usersWithActiveStreak: number;
  avgCurrentStreak: number;
  avgLongestStreak: number;
  totalDistinctPracticeCompletions: number;
  leaderboardOptInRate: number;
  usersByTradition: Record<string, number>;
  usersWithSignupDate: number;
}

export type PracticeMilestone = 0 | 7 | 30 | 100 | 365;
export type OnboardingIntent = 'deepen' | 'explore' | 'routine';
export type ExperienceLevel = 'new' | 'some' | 'experienced';

export interface UpdateMeBody {
  displayName?: string;
  traditions?: Tradition[];
  themePreference?: ThemeId;
  leaderboardOptIn?: boolean;
  gender?: Gender | null;
  primaryTradition?: Tradition | null;
  timezone?: string | null;
  intent?: OnboardingIntent | null;
  experienceLevel?: ExperienceLevel | null;
  timeBudgetMinutes?: number | null;
  locale?: Locale;
}

/** GET /api/today's response -- one deterministic, explainable recommendation. `practiceId` is
 * null only when the caller has no traditions selected or none have any content yet. */
export interface TodayPlan {
  practiceId: string | null;
  reason: 'next_in_path' | 'premium_preview' | 'review' | 'none';
  explanation: string;
  nextStep: string;
}

/** GET /api/schedule's response -- a fixed sequencing of one pillar's already-authored levels
 * across the Development Plan's 30-day flagship structure. Never fabricated content: `type` is
 * "new" only for a level that actually exists; everything past the real ladder is honestly
 * "review"/"application"/"consolidation". */
export interface ThirtyDaySchedule {
  pillarId: string;
  days: ScheduleDay[];
}

export interface ScheduleDay {
  day: number;
  type: 'new' | 'review' | 'application' | 'consolidation';
  practiceId: string | null;
  note: string;
}

// --- Personal routine (Work Package 3) -----------------------------------------------------

export type DailyMoment = 'morning' | 'midday' | 'evening' | 'night';

export interface RoutineItem {
  practiceId: string;
  /** null only when the practice no longer exists (content removed) -- see `access`. */
  title: string | null;
  durationSeconds: number | null;
  access: PracticeAccess;
}

export interface Routine {
  items: RoutineItem[];
  dailyMoment: DailyMoment | null;
  totalDurationSeconds: number;
}

export interface UpdateRoutineBody {
  practiceIds?: string[];
  dailyMoment?: DailyMoment | null;
}

// --- Private journal (Work Package 3) ------------------------------------------------------

export interface JournalEntry {
  entryId: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  practiceId: string | null;
}

export interface JournalEntriesResponse {
  entries: JournalEntry[];
}

export interface CreateJournalEntryBody {
  text: string;
  practiceId?: string | null;
}

export interface UpdateJournalEntryBody {
  text?: string;
  practiceId?: string | null;
}

export interface Quote {
  quoteId: string;
  text: string;
  author: string;
  tradition: Tradition;
}

export interface TodayQuotes {
  date: string;
  quotes: Quote[];
}

// The "skill types" every tradition's path is built from -- deliberately generic (not
// tradition-specific vocabulary) so the same four categories scale across all 17 traditions:
// - reading: a sacred/foundational text presented slowly, with reflection
// - prayer: a guided prayer/devotional, spoken by the mascot
// - meditation: silent or guided stillness practice
// - service: a real-world reflection or action prompt (the one skill that isn't audio-first)
// - movement: embodied instruction (e.g. qi gong/tai chi forms) -- taxonomy only for now, no
//   seed content is tagged with it yet (Work Package 2's "add movement as an explicit activity
//   type... instead of forcing it into stillness").
export type PracticeType = 'reading' | 'prayer' | 'meditation' | 'service' | 'movement';

export const PRACTICE_TYPE_LABELS: Record<PracticeType, string> = {
  reading: 'Reading',
  prayer: 'Prayer',
  meditation: 'Meditation',
  service: 'Service',
  movement: 'Movement',
};

export const PRACTICE_TYPE_ICONS: Record<PracticeType, string> = {
  reading: '📖',
  prayer: '🙏',
  meditation: '🧘',
  service: '🤝',
  movement: '🕺',
};

export interface Practice {
  practiceId: string;
  title: string;
  type: PracticeType;
  tradition: Tradition;
  /** Which Pillar this level belongs to -- null/undefined for a tradition not yet migrated onto
   * the pillar model, which falls back to the legacy flat duration-ordered path client-side. */
  pillarId?: string | null;
  /** Position within pillarId's ladder, 1-indexed. Absent for legacy (non-pillar) practices. */
  level?: number | null;
  durationSeconds: number;
  description: string;
  /** The full narration script, for an optional in-player transcript (Work Package 4). Protected
   * the same way the audio itself is: null unless `access === 'available'`, so a locked level's
   * script can never be read as a paywall bypass via the list endpoint. */
  transcript: string | null;
  /** Server-computed: true when this level is beyond the free tier's cap and the caller isn't
   * Premium. Kept for backward compatibility -- exactly `access === 'premium_required'`; prefer
   * `access` in new code, since it also distinguishes a progression lock from a paywall lock. */
  premiumLocked: boolean;
  /** The canonical access state (backend/app/domain.py's resolve_practice_access), computed
   * identically here, on the audio endpoint, and on session completion, so all three routes
   * agree on what's actually playable. */
  access: PracticeAccess;
  /** Content-review lifecycle (Work Package 2). Defaults are honest, not aspirational: "draft"
   * unless a real, named, qualified reviewer's review is recorded -- never inferred from a
   * practice simply being live. See docs/IMPLEMENTATION_LEDGER.md's Work Package 2 section. */
  contentVersion: number;
  circle: number;
  publicationState: 'draft' | 'published' | 'unpublished';
  reviewerStatus: 'draft' | 'reviewed';
  reviewerName: string | null;
  sourceRefs: string[];
  outcome: string | null;
  guidanceRequired: boolean;
}

export type PracticeAccess = 'available' | 'premium_required' | 'prerequisite_required' | 'unpublished';

export interface PracticesResponse {
  practices: Practice[];
}

/** A named practice-lineage within a tradition (e.g. Christian "Prayer"), tagged with exactly
 * one PracticeType. Not every tradition has pillar rows yet -- see Practice.pillarId. */
export interface Pillar {
  pillarId: string;
  tradition: Tradition;
  practiceType: PracticeType;
  title: string;
  description: string;
  order: number;
}

export interface PillarsResponse {
  pillars: Pillar[];
}

/** A religious observance/festival on a given date. `movable` flags a lunar/solar-shifting date
 * (Ramadan, Easter, Diwali, etc.) that needs annual re-seeding, vs. a fixed one (Christmas). */
export interface CalendarEvent {
  eventId: string;
  tradition: Tradition;
  title: string;
  description: string;
  /** ISO date, e.g. "2026-12-25". */
  date: string;
  movable: boolean;
  /** Work Package 3's calendar richness -- null means "not yet cataloged", never fabricated. */
  region: string | null;
  community: string | null;
  startTime: string | null;
  uncertainty: 'confirmed' | 'estimated';
  /** Links to this tradition's flagship pillar, when it has one -- lets the UI offer a direct
   * route to an eligible practice for this observance. */
  relatedPillarId: string | null;
}

export interface CalendarResponse {
  events: CalendarEvent[];
}

export interface PracticeAudio {
  practiceId: string;
  url: string;
  expiresAt: string;
  cached: boolean;
}

export type BookFormat = 'pdf' | 'epub' | 'txt';

export interface Book {
  bookId: string;
  title: string;
  tradition: Tradition;
  format: BookFormat;
  license: string;
}

export interface LibraryResponse {
  books: Book[];
}

export interface LibraryDownload {
  bookId: string;
  url: string;
  expiresAt: string;
}

export interface PostSessionBody {
  practiceId: string;
  durationSeconds: number;
}

export interface PostSessionResponse {
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string;
  completedPracticeIds: string[];
  shieldsAvailable: number;
  shieldUsed: boolean;
  pilgrimRank: PilgrimRank;
  totalPracticeDays: number;
  practiceMilestone: PracticeMilestone;
}

export interface ReinforcementResponse {
  shieldsAvailable: number;
}

export interface LeaderboardEntry {
  displayName: string;
  currentStreak: number;
  rank: number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
}

// --- Billing (Work Package 5, Whop) --------------------------------------------------------

/** POST /api/billing/checkout's response -- a URL to redirect the browser to. `tier` only ever
 * actually changes later, via a server-verified Whop webhook, never from this response or the
 * checkout return redirect -- see backend/app/handlers/billing.py. */
export interface CreateCheckoutResponse {
  checkoutUrl: string;
}

export interface ApiError {
  error: string;
}

/** Structural shape shared by client.ts's real `ApiError` and mock.ts's `MockApiError` -- lets UI
 * code branch on a denied practice-access reason (see `PracticeAccess`) the same way regardless
 * of which one actually threw, without either module importing the other's class (client.ts
 * already imports mock.ts, so the reverse would be circular). */
export interface AccessDeniedError {
  status: number;
  access?: PracticeAccess;
  message: string;
}

export function isAccessDeniedError(e: unknown): e is AccessDeniedError {
  return (
    !!e &&
    typeof e === 'object' &&
    'status' in e &&
    typeof (e as { status: unknown }).status === 'number'
  );
}
